import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === 'string') {
      return message;
    }
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unable to sign in';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { configured, signIn, user, role, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      navigate(role === 'admin' ? '/admin' : '/dashboard', { replace: true });
    }
  }, [user, role, loading, navigate]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const redirectQuery = new URLSearchParams(location.search).get('redirect');
  const safeQueryRedirect =
    redirectQuery &&
    redirectQuery.startsWith('/') &&
    !redirectQuery.startsWith('//')
      ? redirectQuery
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!configured) {
      return;
    }

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    const from =
      location.state &&
      typeof location.state === 'object' &&
      'from' in location.state
        ? location.state.from
        : null;
    const stateRedirectTo =
      from &&
      typeof from === 'object' &&
      'pathname' in from &&
      typeof from.pathname === 'string'
        ? `${from.pathname}${'search' in from && typeof from.search === 'string' ? from.search : ''}`
        : null;

    try {
      setSubmitting(true);
      const profile = await signIn(email, password);

      if (!profile) {
        setError('Signed in, but no profile row was returned for this user.');
        return;
      }

      const fallbackPath = profile.role === 'admin' ? '/admin' : '/dashboard';
      navigate(safeQueryRedirect ?? stateRedirectTo ?? fallbackPath, {
        replace: true,
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-surface-200 p-6 md:p-8 shadow-sm">
          <div className="text-center mb-6">
            <img
              src="https://public.readdy.ai/ai/img_res/4df53d9e-ba4b-4694-9f59-a23ba95942ed.png"
              alt="Motionsdio"
              className="w-12 h-12 rounded mx-auto mb-3 object-contain"
            />
            <h1 className="text-xl font-bold text-surface-900">Welcome back</h1>
            <p className="text-sm text-surface-500 mt-1">
              Sign in to your Motionsdio account
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {!configured && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              Supabase environment variables are missing. Add them before
              production deployment.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-brand-600 rounded"
                />
                <span className="text-sm text-surface-600">Remember me</span>
              </label>
              <span className="text-sm text-brand-600 hover:text-brand-700 cursor-pointer">
                Forgot password?
              </span>
            </div>
            <button
              type="submit"
              disabled={submitting || !configured}
              className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors">
              {submitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-surface-500">
              Do not have an account?{' '}
              <Link
                to="/register"
                className="text-brand-600 hover:text-brand-700 font-medium">
                Create one
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-surface-200">
            <p className="text-xs text-surface-400 text-center">
              Use the account created in your production auth system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
